// src/contexts/AuthContext.js
import { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [initialCheckDone, setInitialCheckDone] = useState(false);

    const checkSession = async () => {
        try {
            const response = await api.get('/profile/');
            setUser(response.data);
            setIsAuthenticated(true);
            return true;
        } catch (error) {
            setUser(null);
            setIsAuthenticated(false);
            return false;
        }
    };

    const login = async (username, password) => {
        console.log('Starte Login-Prozess...');

        try {
            const response = await api.post('/login/', { 
                username, 
                password 
            });
            
            console.log('Login erfolgreich:', response.status);

            setIsAuthenticated(true);
            setUser(response.data.user || response.data);

            return true;
        } catch (error) {
            console.error('Login fehlgeschlagen:', error.response || error);
            setIsAuthenticated(false);
            setUser(null);
            
            if (error.response?.status === 401) {
                throw new Error('Ungültige Anmeldedaten. Bitte überprüfen Sie Ihren Benutzernamen und Ihr Passwort.');
            } else if (error.response?.status === 403) {
                throw new Error('Zugriff verweigert. Bitte wenden Sie sich an Ihren Administrator.');
            } else if (!navigator.onLine) {
                throw new Error('Keine Internetverbindung. Bitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.');
            } else {
                throw new Error('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
            }
        }
    };

    const logout = async () => {
        try {
            await api.post('/logout/');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUser(null);
            setIsAuthenticated(false);
        }
    };

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                setLoading(true);
                await checkSession();
            } catch (error) {
                console.error('Session check failed:', error);
            } finally {
                setLoading(false);
                setInitialCheckDone(true);
            }
        };

        if (!initialCheckDone) {
            initializeAuth();
        }
    }, [initialCheckDone]);

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                error,
                isAuthenticated,
                login,
                logout,
                checkSession,
                initialCheckDone
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
